import type React from "react";
import { useId } from "react";
import { twJoin } from "tailwind-merge";

import { Inline } from "@/design-system/Inline";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";
import { RuntimeColorSwatch } from "@/ui-adapters/RuntimeColorBadge";

import { ChartPatterns } from "./ChartPatterns";

type ChartLegendItem = {
  label: string | React.ReactNode;
  tooltip?: string;
  color: string;
  pattern?: "solid" | "striped";
};

type ChartLegendProps = {
  items: ChartLegendItem[];
  vertical?: boolean;
};

export function ChartLegend({ items, vertical }: ChartLegendProps): React.ReactNode {
  const idPatternStripes = useId();

  return (
    <section
      className={twResolve(
        "mx-auto flex w-fit max-w-full flex-col flex-wrap items-start gap-x-4 gap-y-2",
        items.length > 3 && "@sm:grid @sm:grid-cols-2",
        !vertical && "@sm:flex-row",
      )}
    >
      {items.map((item, index) => {
        const isIndicatorSmall = items.length > 2;
        const patternId = `${idPatternStripes}-${index}`;

        return (
          <Tooltip key={item.color + "-" + item.pattern} tooltip={item.tooltip}>
            <Inline gap="sm" align="center" wrap={false}>
              {item.pattern === "striped" && (
                <span
                  className={twJoin(
                    "shrink-0 overflow-hidden rounded-xs",
                    isIndicatorSmall ? "size-status-dot" : "size-icon-inline",
                  )}
                >
                  <svg>
                    <ChartPatterns.Stripes id={patternId} color={item.color} />
                    <rect width="100%" height="100%" fill={`url(#${patternId})`} />
                  </svg>
                </span>
              )}
              {item.pattern !== "striped" && (
                <RuntimeColorSwatch color={item.color} size={isIndicatorSmall ? "compact" : "default"} />
              )}

              <span
                className={twJoin(
                  "text-left whitespace-pre-line text-text-primary",
                  items.length > 2 ? "text-overline" : "text-caption",
                )}
              >
                {item.label}
              </span>
            </Inline>
          </Tooltip>
        );
      })}
    </section>
  );
}
